import React, { useState, useEffect } from 'react';
import { Field, ErrorMessage } from 'formik';
import { nikshayAPI } from '../../../../services/nikshay.api';

// Module-level cache to prevent duplicate simultaneous/subsequent network requests
const apiRequestCache = {};

/**
 * Generic HierarchyField component to handle State, District, Lab, etc.
 * 
 * @param {Object} props - Component props
 * @param {string} props.name - Formik field name
 * @param {string} props.keyName - Formik field name for the ID key
 * @param {string} props.label - Field label text
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {Function} props.setFieldValue - Formik setFieldValue function
 * @param {Function} props.onChange - Custom onChange handler (optional)
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.values - Formik values object
 * @param {string} props.apiMethod - Which nikshayAPI method to call (e.g. 'getHierarchyChildren')
 * @param {string|number} props.parentId - The ID to pass to the API
 */
const HierarchyField = ({
  name,
  keyName,
  levelName,
  label,
  required = false,
  className = "",
  disabled = false,
  setFieldValue,
  onChange = null,
  placeholder = "Select Option",
  values = {},
  apiMethod = 'getHierarchyChildren',
  parentId = null,
  enabledValue = null,
  staticOptionKey = null,
  staticOptionName = null,
  staticOptionLevel = null
}) => {
  const [options, setOptions] = useState([]);

  // Fetch data dynamically based on generic props
  const testType = values?.TestType || '';

  useEffect(() => {
    if (staticOptionKey && staticOptionName) {
      setOptions([{
        ID: staticOptionKey,
        Name: staticOptionName,
        Level: staticOptionLevel
      }]);
      return;
    }

    const fetchOptions = async () => {
      // Only fetch if we have a valid parentId (or if parentId is explicitly 1 for root)
      if (parentId === undefined || parentId === null || parentId === "") {
        setOptions([]);
        return;
      }
      
      const cacheKey = apiMethod === 'getHierarchyFilters'
        ? `${apiMethod}_${parentId}_${testType}`
        : `${apiMethod}_${parentId}`;
      
      try {
        if (!apiRequestCache[cacheKey]) {
          apiRequestCache[cacheKey] = nikshayAPI[apiMethod](parentId, testType)
            .catch(error => {
              // Remove failed promise so subsequent requests can try again
              delete apiRequestCache[cacheKey];
              throw error;
            });
        }
        
        const response = await apiRequestCache[cacheKey];
        const data = Array.isArray(response) ? response : (response?.data || []);
        setOptions(data);
      } catch (error) {
        console.error(`Failed to fetch hierarchy options for ${label}:`, error);
        setOptions([]);
      }
    };
    
    fetchOptions();
  }, [parentId, apiMethod, staticOptionKey, staticOptionName, staticOptionLevel, testType]);

  const getTarget = (item) => item?.hierarchy || item;
  const getId = (item) => {
    const target = getTarget(item);
    return target?.ID || target?.id || target?.Id || target?.value || target?.key || target?.Key || "";
  };
  const getName = (item) => {
    const target = getTarget(item);
    return target?.Name || target?.name || target?.label || target?.text || target?.Value || "";
  };
  const getLevel = (item) => {
    const target = getTarget(item);
    return target?.Level || target?.level || null;
  };

  // Get current value from nested object structure
  const getCurrentValue = () => {
    const normalizedPath = name.replace(/\[(\d+)\]/g, '.$1');
    const fieldParts = normalizedPath.split('.');
    let currentValue = values;
    
    for (const part of fieldParts) {
      if (part === "") continue;
      currentValue = currentValue?.[part];
    }
    
    if (currentValue && typeof currentValue === 'object' && currentValue.Value) {
      return currentValue.Value;
    }
    
    return currentValue || "";
  };

  const handleChange = (e) => {
    const selectedName = e.target.value;
    const selectedOption = e.target.options[e.target.selectedIndex];
    const selectedKey = selectedOption.getAttribute('data-key');
    
    // Find the full option object to get its actual level
    const matchedOption = options.find(opt => String(getId(opt)) === String(selectedKey));
    const selectedLevel = matchedOption ? getLevel(matchedOption) : null;
    
    // Handle nested object structure (.Value) vs flat string
    if (name.endsWith('.Value')) {
      const baseFieldName = name.substring(0, name.lastIndexOf('.Value'));
      setFieldValue(baseFieldName, {
        Value: selectedName,
        Key: selectedKey,
        Level: selectedLevel
      });
    } else {
      setFieldValue(name, selectedName);
      if (keyName) {
        setFieldValue(keyName, selectedKey);
      }
      if (levelName) {
        setFieldValue(levelName, selectedLevel);
      }
    }
    
    // Trigger any dependent field resets or side effects
    if (onChange) {
      onChange(selectedName, selectedKey);
    }
  };

  // Base field name for ErrorMessage
  const baseErrorName = name.endsWith('.Value') 
    ? name.substring(0, name.lastIndexOf('.Value')) 
    : name;

  return (
    <div className="form-group">
      <label htmlFor={name} className="form-label">
        {required && <span style={{ color: "red", fontSize: "20px" }}>*</span>}
        {label}
      </label>
      
      <Field
        as="select"
        id={name}
        name={name}
        className={`form-control input-width select ${className}`}
        disabled={disabled || !parentId}
        value={getCurrentValue()}
        onChange={handleChange}
      >
        <option value="" disabled>{ placeholder}</option>
        {options
          .filter((option) => !enabledValue || getName(option).toLowerCase() === enabledValue.toLowerCase())
          .map((option, idx) => {
            const optionName = getName(option);
            return (
              <option 
                key={idx} 
                value={optionName} 
                data-key={getId(option)} 
              >
                {optionName}
              </option>
            );
          })
        }
      </Field>
      
      <ErrorMessage name={baseErrorName} component="div" className="text-danger" />
    </div>
  );
};

export default HierarchyField;
